import * as React from "react";
import { cn } from "../../lib/utils";

/**
 * SegmentedToggle - High-contrast 2-option toggle component (ONLY toggle type used)
 * 
 * LOCKED VISUAL REQUIREMENTS:
 * - Container pill with visible border
 * - ACTIVE: filled background (theme color), white/near-white label
 * - INACTIVE: transparent/light tint, label = theme color (NOT gray)
 * - Unmistakable in <1 second, no hover dependency
 * 
 * Usage:
 * <SegmentedToggle
 *   options={[
 *     { value: 'login', label: 'Sign In' },
 *     { value: 'register', label: 'Sign Up' }
 *   ]}
 *   value={activeTab}
 *   onChange={setActiveTab}
 *   variant="auth" // 'auth' (green) | 'billing' (orange)
 * />
 */

const VARIANTS = {
  auth: {
    activeBackground: '#7A8F5B', // Primary Green
    activeText: '#FFFFFF',
    inactiveText: '#7A8F5B', // Brand color, NOT gray
    inactiveBg: 'rgba(122, 143, 91, 0.1)', // Very light green tint
    borderColor: '#7A8F5B',
  },
  billing: {
    activeBackground: '#D98A3A', // Secondary Orange
    activeText: '#FFFFFF',
    inactiveText: '#D98A3A', // Brand color, NOT gray
    inactiveBg: 'rgba(217, 138, 58, 0.1)', // Very light orange tint
    borderColor: '#D98A3A',
  },
  default: {
    activeBackground: '#7A8F5B',
    activeText: '#FFFFFF',
    inactiveText: '#7A8F5B',
    inactiveBg: 'rgba(122, 143, 91, 0.1)',
    borderColor: '#7A8F5B',
  },
};

const SegmentedToggle = React.forwardRef(({ 
  options, 
  value, 
  onChange, 
  variant = 'default',
  className,
  ...props 
}, ref) => {
  const colors = VARIANTS[variant] || VARIANTS.default;

  return (
    <div
      ref={ref}
      role="tablist"
      className={cn(
        "inline-flex items-center rounded-full p-1 gap-0",
        className
      )}
      style={{
        backgroundColor: colors.inactiveBg,
        border: `2px solid ${colors.borderColor}`,
        maxWidth: '320px',
      }}
      {...props}
    >
      {options.map((option) => {
        const isActive = value === option.value;
        return (
          <button
            key={option.value}
            role="tab"
            type="button"
            aria-selected={isActive}
            data-testid={option.testId || `toggle-${option.value}`}
            onClick={() => onChange(option.value)}
            className={cn(
              "relative px-6 py-3 text-sm font-bold rounded-full transition-all duration-200",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
              "min-w-[120px] min-h-[44px]" // Mobile-first tap targets
            )}
            style={{
              backgroundColor: isActive ? colors.activeBackground : 'transparent',
              color: isActive ? colors.activeText : colors.inactiveText,
              boxShadow: isActive ? '0 2px 4px rgba(0,0,0,0.15)' : 'none',
            }}
          >
            {option.label}
            {option.badge && (
              <span 
                className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full"
                style={{ 
                  backgroundColor: isActive ? 'rgba(255,255,255,0.25)' : colors.inactiveBg,
                  color: isActive ? colors.activeText : colors.inactiveText,
                }}
              >
                {option.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
});

SegmentedToggle.displayName = "SegmentedToggle";

/**
 * SwitchToggle - High-contrast on/off toggle switch
 * 
 * LOCKED REQUIREMENTS:
 * - On = Primary Green #7A8F5B
 * - Off = neutral but readable (NOT faint gray)
 * - Dark mode readable
 */

const SwitchToggle = React.forwardRef(({ 
  checked, 
  onChange, 
  label,
  disabled = false,
  className,
  ...props 
}, ref) => {
  const activeColor = '#7A8F5B'; // Primary Green
  const inactiveColor = '#6B7280'; // Readable neutral (not faint)

  return (
    <label 
      ref={ref}
      className={cn(
        "inline-flex items-center gap-3 cursor-pointer",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
      {...props}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={cn(
          "relative w-12 h-7 rounded-full transition-colors duration-200",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        )}
        style={{
          backgroundColor: checked ? activeColor : inactiveColor,
          border: `2px solid ${checked ? activeColor : inactiveColor}`,
        }}
      >
        {/* Visual indicators */}
        <span 
          className="absolute inset-0 flex items-center justify-between px-1.5 text-xs font-bold"
          style={{ color: 'rgba(255,255,255,0.7)' }}
        >
          <span style={{ opacity: checked ? 1 : 0 }}>✓</span>
          <span style={{ opacity: checked ? 0 : 1 }}>○</span>
        </span>
        
        {/* Thumb */}
        <span
          className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200"
          style={{
            transform: checked ? 'translateX(22px)' : 'translateX(2px)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
        />
      </button>
      
      {label && (
        <span 
          className="text-sm font-medium select-none"
          style={{ color: 'var(--fy-text-primary)' }}
        >
          {label}
        </span>
      )}
    </label>
  );
});

SwitchToggle.displayName = "SwitchToggle";

export { SegmentedToggle, SwitchToggle };
